"use client";

import React, { type FC, useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import DatePicker from "@/src/shared/components/DatePicker";
import StayDatesFooter from "@/src/shared/components/StayDatesFooter";
import { IOS_PUSH } from "@/src/shared/constants/animation";
import type { IFilter } from "@/src/shared/features/search/types";
import { type BlockedDate } from "@/src/shared/types/blockedDate";
import { useLockBodyScroll } from "@/src/shared/hooks/useLockBodyScroll";

interface IBookingDateModalMobileProps {
  onBack: () => void;
  filters: IFilter;
  setFilters: React.Dispatch<React.SetStateAction<IFilter>>;
  onSave?: () => void;
  blockedDates?: BlockedDate[];
  checkoutOnlyDates?: string[]; // newly added
  /**
   * "app": the app's select date screen — pushed in from the right, months scrolling
   * down, "Check In | N Days | Check Out" over a pink Done. Listing's Book Now uses it.
   */
  appearance?: "default" | "app";
  /**
   * The app's select date screen returns its pick only on Done: with this set the screen
   * edits a copy, hands it over on Done, and Back leaves the stay as it was.
   */
  onDoneDates?: (dates: {
    checkIn: Date | null;
    checkOut: Date | null;
  }) => void;
  doneLabel?: string;
}

const BookingDateModal: FC<IBookingDateModalMobileProps> = ({
  blockedDates,
  checkoutOnlyDates = [],
  onBack,
  filters,
  setFilters,
  onSave,
  appearance = "default",
  onDoneDates,
  doneLabel,
}) => {
  useLockBodyScroll();
  const [draft, setDraft] = useState({
    checkIn: filters.checkIn,
    checkOut: filters.checkOut,
  });
  const dates = onDoneDates
    ? draft
    : { checkIn: filters.checkIn, checkOut: filters.checkOut };
  const changeDates = (next: {
    checkIn: Date | null;
    checkOut: Date | null;
  }) =>
    onDoneDates
      ? setDraft((prev) => ({ ...prev, ...next }))
      : setFilters((f) => ({ ...f, ...next }));
  const save = () => {
    if (onDoneDates) {
      onDoneDates(draft);
      return;
    }
    if (onSave) {
      onSave();
    }
    onBack();
  };

  if (appearance === "app") {
    return (
      <motion.div
        initial={{ x: "100%" }}
        animate={{ x: 0 }}
        exit={{ x: "100%" }}
        transition={IOS_PUSH}
        className="fixed inset-0 z-50 flex flex-col bg-white pt-[env(safe-area-inset-top)]"
      >
        <div className="relative flex h-14 shrink-0 items-center justify-center px-6">
          <button
            type="button"
            onClick={onBack}
            aria-label="Back"
            className="absolute left-4 flex h-10 w-10 items-center justify-center"
          >
            <img
              src="/icons/listing/ios_arrow_back.svg"
              alt=""
              className="h-10 w-10"
            />
          </button>
          <h2 className="text-18 font-semibold text-black">Select Dates</h2>
        </div>
        <div className="scrollbar-hide min-h-0 flex-1 overflow-y-auto overscroll-contain px-8">
          <DatePicker
            appearance="app"
            isDetailsPage={true}
            selectedDates={dates}
            onDateChange={changeDates}
            blockedDates={blockedDates || []}
            checkoutOnlyDates={checkoutOnlyDates}
          />
        </div>
        <div className="border-t border-[#C3C6D3] px-4 pt-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
          <StayDatesFooter
            checkIn={dates.checkIn}
            checkOut={dates.checkOut}
            onDone={save}
            label={doneLabel}
          />
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ y: "100%" }}
      animate={{ y: 0 }}
      exit={{ y: "100%" }}
      transition={{ type: "spring", damping: 30, stiffness: 300 }}
      className="fixed inset-0 bg-white z-50 p-4"
    >
      <div className="flex items-center justify-between pb-1 border-b border-gray-200">
        <button type="button" onClick={onBack} className="p-2">
          <ArrowLeft size={24} />
        </button>
        <h2 className="text-lg font-semibold">Select Date</h2>
        <div></div>
      </div>
      <DatePicker
        selectedDates={{ checkIn: filters.checkIn, checkOut: filters.checkOut }}
        onDateChange={(dates) => setFilters((f) => ({ ...f, ...dates }))}
        containerClass="border-none shadow-none"
        blockedDates={blockedDates || []}
        checkoutOnlyDates={checkoutOnlyDates}
      />
      <div>
        <button
          type="button"
          onClick={save}
          className="w-full bg-gray-900 text-white py-2 mb-2 rounded-lg font-medium shadow-sm"
        >
          Save
        </button>
      </div>
    </motion.div>
  );
};

export default BookingDateModal;
